import { applyCompletion, habitInstanceForCompletion, scheduleAfterCompletion } from './completion';
import { statsAfterUndo, taskAfterUndo, xpToReverse } from './undo';
import { activeDates } from './habit-stats';
import { activityFacts, effectiveStats, newlyEarned } from './badges';
/** Which day's plan a completion touches. */
function planDate(task, today, forDate) {
    // A habit is credited to the day it was FOR — "I did my run yesterday"
    // ticks yesterday. A plain task is always today's plan.
    return task.is_habit ? (forDate || today) : today;
}
const unchanged = (task, stats, scheduleDate) => ({
    changed: false,
    task,
    habitInstance: null,
    removeInstance: null,
    stats,
    schedule: null,
    scheduleDate,
    xpEarned: 0,
    newBadges: [],
});
/** Everything that happens when something is marked done. */
export function completeTaskEffect(request) {
    const { task, getSchedule, stats, today, forDate, actualMinutes = null, now = new Date().toISOString(), } = request;
    const date = planDate(task, today, forDate);
    const schedule = getSchedule(date) ?? null;
    let completed;
    let habitInstance = null;
    if (task.is_habit) {
        // Already ticked for that day — a second press must not pay twice.
        if (request.habitInstances.some((i) => i.habit_id === task.id && i.date === date)) {
            return unchanged(task, stats, date);
        }
        // A habit's status NEVER becomes 'completed'. The HabitInstance is the
        // completion record; setting the status would retire the habit forever,
        // and it would look like it worked.
        completed = { ...task, status: 'todo', actual_minutes: actualMinutes, updated_at: now };
        const slot = schedule?.scheduled_tasks?.find((st) => !st.is_buffer && st.task_id === task.id) ?? null;
        habitInstance = habitInstanceForCompletion(task, date, { slot });
        if (request.workoutId)
            habitInstance = { ...habitInstance, workout_id: request.workoutId };
    }
    else {
        if (task.status === 'completed')
            return unchanged(task, stats, date);
        completed = {
            ...task,
            status: 'completed',
            actual_minutes: actualMinutes,
            completed_at: now,
            updated_at: now,
        };
    }
    const effect = applyCompletion(stats, completed, today);
    // Recorded on the task so undo takes back exactly what was paid. Editing a
    // task's difficulty afterwards and recomputing would hand back a different
    // number, drifting the running total every time.
    if (!task.is_habit)
        completed = { ...completed, xp_awarded: effect.xpEarned };
    // Settled against the history this completion leaves behind — the badges
    // that need it (perfect days, a habit's run, early bird…) and any total the
    // running record had lost. See services/badges.ts.
    //
    // Badges are settled from the ones held BEFORE this completion, so every
    // badge it earns — applyCompletion's included — gets its dated
    // badge_history entry (the earned date Badges & XP shows).
    const settled = effectiveStats({ ...effect.stats, earned_badges: stats.earned_badges ?? [] }, activityFacts({
        tasks: task.is_habit ? request.tasks : request.tasks.map((t) => (t.id === task.id ? completed : t)),
        habitInstances: habitInstance ? [...request.habitInstances, habitInstance] : request.habitInstances,
        today,
        timezone: request.timezone,
    }), now);
    return {
        changed: true,
        task: completed,
        habitInstance,
        removeInstance: null,
        stats: settled,
        schedule: schedule ? scheduleAfterCompletion(schedule, task.id, true) : null,
        scheduleDate: date,
        xpEarned: effect.xpEarned,
        newBadges: newlyEarned(stats, settled, now),
        newLevel: settled.level > stats.level ? settled.level : undefined,
    };
}
/** Everything that happens when a completion is taken back. */
export function uncompleteTaskEffect(request) {
    const { task, tasks, habitInstances, getSchedule, stats, today, forDate, now = new Date().toISOString(), } = request;
    const date = planDate(task, today, forDate);
    const schedule = getSchedule(date) ?? null;
    let restored = task;
    let xpAwarded;
    let removeInstance = null;
    let instancesAfter = habitInstances;
    if (task.is_habit) {
        const instance = habitInstances.find((i) => i.habit_id === task.id && i.date === date);
        // Nothing recorded for that day — don't touch the totals.
        if (!instance)
            return unchanged(task, stats, date);
        xpAwarded = xpToReverse(task, instance.xp_awarded);
        removeInstance = { habitId: task.id, date };
        instancesAfter = habitInstances.filter((i) => !(i.habit_id === task.id && i.date === date));
    }
    else {
        if (task.status !== 'completed')
            return unchanged(task, stats, date);
        xpAwarded = xpToReverse(task, task.xp_awarded);
        restored = taskAfterUndo(task, now);
    }
    // Built from POST-undo data: the streak is recomputed from the dates that
    // remain rather than decremented, so undoing one of three things done today
    // doesn't touch it and undoing the only one ends it.
    const tasksAfter = task.is_habit
        ? tasks
        : tasks.map((t) => (t.id === task.id ? restored : t));
    return {
        changed: true,
        task: restored,
        habitInstance: null,
        removeInstance,
        // Taken back from the stored total AND the history, then settled — so
        // XP and counts fall, while badges and the record streak stay.
        stats: effectiveStats(statsAfterUndo({
            stats,
            xpAwarded,
            isHabit: Boolean(task.is_habit),
            activeDatesAfter: activeDates(tasksAfter, instancesAfter, request.timezone),
            today,
        }), activityFacts({ tasks: tasksAfter, habitInstances: instancesAfter, today, timezone: request.timezone }), now),
        schedule: schedule ? scheduleAfterCompletion(schedule, task.id, false) : null,
        scheduleDate: date,
        xpEarned: -xpAwarded,
        newBadges: [],
    };
}
//# sourceMappingURL=completion-effect.js.map